import React from 'react';
import { Button, Divider, Select, InputNumber, Checkbox, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import ConfigurationLayout from '../configuration.layout';
import { getUserTenureOptions, getOverdraftOptions, getAchOptions, getBalanceOptions, sanitizeBalanceRules } from '../utils/available-balance-options';
import styles from './styles/available-balance-step.module.scss';

const { Option } = Select;
const USDFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
});

function AvailableBalanceStep({
    state,
    firstTimeSetup,
    setThreshold,
    setDoubleDipCap,
    addRule,
    removeRule,
    updateRule,
    toggleRuleGroup,
    getAvailableBalanceSummary,
    hasChanges,
    onSubmit,
    onContinue,
    onCancel,
    onStepChange
}) {
    const { threshold = 0.75, doubleDipCap = 0.5, balance_rules, balance_enabled = {}, segment_policy = {}, segmentation_enabled = {}, loading, user } = state;
    const sanitizedBalanceRules = sanitizeBalanceRules(balance_rules, segment_policy, segmentation_enabled);
    const {
        userTenureRules = [{ id: 1, condition: '60-89-days', deduction: 10 }],
        overdraftRules = [],
        achRules = [],
        employmentRules = [],
        balanceRules = []
    } = sanitizedBalanceRules || {};
    const userTenureOptions = getUserTenureOptions(segment_policy, segmentation_enabled);
    const overdraftOptions = getOverdraftOptions(segment_policy, segmentation_enabled);
    const achOptions = getAchOptions(segment_policy, segmentation_enabled);
    const balanceOptions = getBalanceOptions(segment_policy, segmentation_enabled);
    // Convert decimal to percentage for display (0.75 -> 75)
    // Round to avoid floating point precision issues (e.g., 0.7 * 100 = 70.00000000000001)
    // Allow null/undefined to show empty field
    const thresholdDisplay = threshold !== null && threshold !== undefined ? Math.round(threshold * 100) : undefined;
    const doubleDipDisplay = doubleDipCap !== null && doubleDipCap !== undefined ? Math.round(doubleDipCap * 100) : undefined;
    const advancedConfigurations = [
        {
            key: 'userTenure',
            title: 'User Tenure',
            description: "User's amount of time banking at your institution",
            rules: userTenureRules,
            enabled: balance_enabled.userTenure ?? false,
            conditionType: 'tenure',
            options: userTenureOptions,
            defaultCondition: userTenureOptions[0]?.value
        },
        {
            key: 'overdraft',
            title: 'Overdraft & NSF Instances',
            description: 'Number of overdraft or NSF instances your eligible customers have done in the past 180 days.',
            rules: overdraftRules,
            enabled: balance_enabled.overdraft ?? false,
            conditionType: 'count',
            options: overdraftOptions,
            defaultCondition: overdraftOptions[0]?.value
        },
        {
            key: 'ach',
            title: 'ACH Return & Chargeback Instances',
            description: 'Number of ACH return or chargeback instances your eligible customers have done in the past 180 days.',
            rules: achRules,
            enabled: balance_enabled.ach ?? false,
            conditionType: 'count',
            options: achOptions,
            defaultCondition: achOptions[0]?.value
        },
        {
            key: 'employment',
            title: 'Employment Type',
            description: "Your user's employment type",
            rules: employmentRules,
            enabled: balance_enabled.employment ?? false,
            conditionType: 'employment',
            options: [
                { value: 'full-time', label: 'Full-time' },
                { value: 'part-time', label: 'Part-time' },
                { value: 'contract', label: 'Contract' }
            ],
            defaultCondition: 'full-time'
        },
        {
            key: 'balance',
            title: 'Avg. Daily Balance',
            description: "User's average daily balance",
            rules: balanceRules,
            enabled: balance_enabled.balance ?? false,
            conditionType: 'balance',
            options: balanceOptions,
            defaultCondition: balanceOptions[0]?.value
        }
    ];
    const summary = getAvailableBalanceSummary();
    const formatPercentage = (value, decimals = 0) => (value === undefined || value === null ? '—' : `${Number(value).toFixed(decimals)}%`);
    const formatDeduction = (value) => {
        if (!value) return '0%';
        const numeric = Number(value);
        const formatted = Math.abs(numeric).toFixed(0);
        return `${numeric > 0 ? '-' : ''}${formatted}%`;
    };
    const baseThresholdPercent = Math.round(thresholdDisplay ?? 0);
    const sampleDeduction = Math.round(summary.userTenureDeduction || 0);
    const calculatedSample = Math.max(0, baseThresholdPercent - sampleDeduction);
    const infoAlertMessage = `How deductions are applied → Available Balance Threshold (${baseThresholdPercent}%) - deduction (${sampleDeduction}%) = ${calculatedSample}% available balance threshold`;
    const warningAlertMessage = `Users that fall out of the threshold segmentation will have your initial Available Balance Threshold (${baseThresholdPercent}%)`;

    const mainContent = (
        <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Configure your user's available balance</h2>
            <Alert
                message="Available Balance to advance = (Gross Pay - Deductions) × Available Balance Threshold"
                type="info"
                showIcon
                className={styles.balanceAlert}
            />
            <div className={styles.formFields}>
                <div className={styles.thresholdSection}>
                    <div className={styles.inputBlock}>
                        <label className={styles.fieldLabel}>Available Balance Threshold</label>
                        <p className={styles.fieldDescription}>The percentage of the accrued net pay your eligible users are able to advance.</p>
                        <div className={styles.thresholdInput}>
                            <InputNumber
                                value={thresholdDisplay}
                                onChange={setThreshold}
                                min={0}
                                max={100}
                                suffix="%"
                                className={styles.thresholdField}
                                placeholder="Enter threshold"
                                formatter={(value) => (value === undefined || value === null || value === '' ? '' : value)}
                                parser={(value) => value}
                            />
                        </div>
                    </div>

                    <Divider className={styles.inputDivider} />

                    <div className={styles.inputBlock}>
                        <label className={styles.fieldLabel}>EWA Double-Dip Cap</label>
                        <p className={styles.fieldDescription}>The percentage of the accrued net pay your eligible users are able to advance.</p>
                        <div className={styles.thresholdInput}>
                            <InputNumber
                                value={doubleDipDisplay}
                                onChange={setDoubleDipCap}
                                min={0}
                                max={100}
                                suffix="%"
                                className={styles.thresholdField}
                                placeholder="Enter double-dip cap"
                                formatter={(value) => (value === undefined || value === null || value === '' ? '' : value)}
                                parser={(value) => value}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.advancedSection}>
                    <div className={styles.advancedHeaderRow}>
                        <span className={styles.advancedSectionLabel}>Advanced Available Balance configurations</span>
                        <span className={styles.optionalTag}>Optional</span>
                    </div>
                    <p className={styles.advancedDescription}>Select the configurations that will further throttle your available balance threshold.</p>
                    <Divider className={styles.sectionDivider} />

                    <div className={styles.notificationStack}>
                        <Alert message={infoAlertMessage} type="info" showIcon className={styles.infoAlert} />
                        <Alert message={warningAlertMessage} type="warning" showIcon className={styles.warningAlert} />
                    </div>
                    <Divider className={styles.sectionDivider} />

                    <div className={styles.configurationList}>
                        {advancedConfigurations.map((section) => {
                            const isEnabled = section.enabled;
                            const hasOptions = section.options && section.options.length > 0;
                            return (
                                <div key={section.key} className={`${styles.configurationItem} ${!isEnabled ? styles.configurationItemDisabled : ''}`}>
                                    <div className={styles.configurationHeader}>
                                        <Checkbox
                                            checked={isEnabled}
                                            onChange={() => toggleRuleGroup(section.key)}
                                            className={styles.configurationCheckbox}
                                            disabled={!hasOptions && !isEnabled}>
                                            <span className={styles.configurationTitle}>{section.title}</span>
                                        </Checkbox>
                                    </div>
                                    <p className={styles.configurationDescription}>{section.description}</p>

                                    {isEnabled && hasOptions && section.rules.length > 0 && <Divider className={styles.configurationDivider} />}

                                    {isEnabled && hasOptions && section.rules.length > 0 && (
                                        <div className={styles.rulesContainer}>
                                            {section.rules.map((rule) => (
                                                <div key={rule.id} className={styles.ruleRow}>
                                                    <div className={styles.ruleTextGroup}>
                                                        <span className={styles.ruleText}>
                                                            if a user has{' '}
                                                            <span className={styles.ruleHighlight}>
                                                                {section.conditionType === 'tenure' && 'a tenure of'}
                                                                {section.conditionType === 'count' && 'completed'}
                                                                {section.conditionType === 'employment' && 'an employment type of'}
                                                                {section.conditionType === 'balance' && 'an average daily balance of'}
                                                            </span>
                                                        </span>
                                                        <Select
                                                            value={rule.condition}
                                                            onChange={(value) => updateRule(section.key, rule.id, 'condition', value)}
                                                            className={styles.conditionSelect}
                                                            disabled={!isEnabled || !hasOptions}>
                                                            {section.options.map((option) => (
                                                                <Option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </div>
                                                    <div className={styles.ruleActionGroup}>
                                                        <span className={styles.ruleText}>their available balance threshold will be reduced by</span>
                                                        <div className={styles.deductionInput}>
                                                            <InputNumber
                                                                value={rule.deduction}
                                                                onChange={(value) => updateRule(section.key, rule.id, 'deduction', value)}
                                                                min={0}
                                                                max={100}
                                                                className={styles.deductionField}
                                                                disabled={!isEnabled || !hasOptions}
                                                                formatter={(value) =>
                                                                    value === undefined || value === null || value === '' ? '' : `${value}%`
                                                                }
                                                                parser={(value) => {
                                                                    if (value === undefined || value === null) return value;
                                                                    return value.replace(/%/g, '');
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <Button
                                                        type="text"
                                                        icon={<DeleteOutlined />}
                                                        className={styles.deleteButton}
                                                        onClick={() => removeRule(section.key, rule.id)}
                                                        disabled={!isEnabled}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {isEnabled && !hasOptions && (
                                        <p className={styles.disabledHelper}>No additional settings are required based on your segmentation.</p>
                                    )}
                                    {!isEnabled && <p className={styles.disabledHelper}>Enable this configuration to add thresholds.</p>}

                                    <Button
                                        type="text"
                                        icon={<PlusOutlined />}
                                        className={styles.addRuleButton}
                                        onClick={() => addRule(section.key, section.defaultCondition)}
                                        disabled={!isEnabled || !hasOptions}>
                                        Add threshold setting row
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );

    // Side content - summary and actions
    const sideContent = (
        <div className={styles.sideContent}>
            <div className={styles.summarySection}>
                <div className={styles.userProfile}>
                    <img src="/user.png" alt="Michael" className={styles.userAvatar} />
                    <p className={styles.userName}>Meet Michael</p>
                </div>

                <div className={styles.summarySection}>
                    <div className={styles.summaryGroup}>
                        <h3 className={styles.summaryTitle}>Available Balance Summary</h3>

                        <div className={styles.summaryBlock}>
                            <h4 className={styles.summaryGroupTitle}>Payroll Breakdown</h4>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Gross Pay</span>
                                <span className={styles.summaryValue}>{USDFormatter.format(summary.grossPay)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>All Payroll Deductibles</span>
                                <span className={styles.summaryValueRed}>-{USDFormatter.format(summary.payrollDeductions)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Net Pay</span>
                                <span className={styles.summaryValue}>{USDFormatter.format(summary.netPay)}</span>
                            </div>
                        </div>

                        <div className={styles.summaryBlock}>
                            <h4 className={styles.summaryGroupTitle}>Threshold Settings</h4>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Available Balance Threshold</span>
                                <span className={styles.summaryValue}>{formatPercentage(summary.threshold)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>EWA Double-Dip Cap</span>
                                <span className={styles.summaryValue}>{formatPercentage(summary.doubleDipCap)}</span>
                            </div>
                        </div>

                        <div className={styles.summaryBlock}>
                            <h4 className={styles.summaryGroupTitle}>Advanced Deductions</h4>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>User Tenure</span>
                                <span className={styles.summaryValueRed}>{formatDeduction(summary.userTenureDeduction)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Overdraft/NSF Instances</span>
                                <span className={styles.summaryValue}>{formatDeduction(summary.overdraftDeduction)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>ACH Return & Chargeback Instances</span>
                                <span className={styles.summaryValue}>{formatDeduction(summary.achDeduction)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Employment Type</span>
                                <span className={styles.summaryValue}>{formatDeduction(summary.employmentDeduction)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Avg. Daily Balance</span>
                                <span className={styles.summaryValue}>{formatDeduction(summary.balanceDeduction)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Available Balance</span>
                                <span className={styles.summaryValue}>{USDFormatter.format(summary.availableBalance)}</span>
                            </div>
                        </div>

                        <div className={styles.summaryBlock}>
                            <h4 className={styles.summaryGroupTitle}>Final Numbers</h4>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Threshold After Deductions</span>
                                <span className={styles.summaryValue}>{formatPercentage(summary.finalThreshold, 2)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Michael's Available Balance</span>
                                <span className={styles.finalBalanceValue}>{USDFormatter.format(summary.availableBalance)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {!firstTimeSetup && (
                    <div className={styles.actionSection}>
                        <Divider className={styles.actionDivider} />
                        {hasChanges() && (
                            <div className={styles.actionButtons}>
                                <Button size="large" className={styles.cancelButton} onClick={onCancel}>
                                    Cancel
                                </Button>
                                <Button type="primary" size="large" loading={loading} onClick={onSubmit} className={styles.continueButton}>
                                    Submit
                                </Button>
                            </div>
                        )}
                    </div>
                )}
                {firstTimeSetup && (
                    <div className={styles.actionSection}>
                        <Divider className={styles.actionDivider} />
                        <div className={styles.actionButtons}>
                            <Button size="large" className={styles.cancelButton} onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button type="primary" size="large" loading={loading} onClick={onContinue} className={styles.continueButton}>
                                Continue
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <ConfigurationLayout isFirstTimeSetUp={firstTimeSetup} currentStep={1} sideContent={sideContent} onStepChange={onStepChange} user={user}>
            {mainContent}
        </ConfigurationLayout>
    );
}

export default AvailableBalanceStep;
